import React, {
  memo,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useDiscountManager } from "../hooks/useDiscountManager";
import useIsMobile from "../hooks/useIsMobile";
import { throttle } from "lodash";
import { useTranslation } from "react-i18next";

const DiscountProgressBar = memo(() => {
  const {
    nextDiscount,
    amountTillNextDiscount,
    discountProgress,
    hasNextDiscount,
    hasAnyDiscounts,
  } = useDiscountManager();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // REF and state for throttled animation of the progress bar itself
  const valuesRef = useRef({
    discountProgress,
    hasNextDiscount,
    nextDiscount,
    amountTillNextDiscount,
    hasAnyDiscounts,
  });
  const [animatedProgress, setAnimatedProgress] = useState(0);

  const throttledUpdate = useCallback(
    throttle((newProgress) => {
      setAnimatedProgress(newProgress);
    }, 50), // 50ms throttle for smoother animation
    []
  );

  useEffect(() => {
    valuesRef.current = {
      discountProgress,
      hasNextDiscount,
      nextDiscount,
      amountTillNextDiscount,
      hasAnyDiscounts,
    };
    throttledUpdate(discountProgress);
  }, [
    discountProgress,
    hasNextDiscount,
    nextDiscount,
    amountTillNextDiscount,
    hasAnyDiscounts,
    throttledUpdate,
  ]);

  // ─────────────────────────────────────────────────────────────────────
  // NEW: We’ll create a piece of state that controls how many pixels
  // from the bottom of the viewport we “push up” the bar.
  // When nothing is overlapping, it should sit 10px above the bottom.
  // If ContactUs has scrolled into view, we increase bottomOffset.
  // ─────────────────────────────────────────────────────────────────────
  const [bottomOffset, setBottomOffset] = useState(10);

  useEffect(() => {
    const BAR_HEIGHT = 0; // px

    const handleScroll = () => {
      const contactEl = document.getElementById("contact");
      if (!contactEl) {
        // If the ContactUs element is not found, just keep the offset at 10
        setBottomOffset(10);
        return;
      }

      // Get the top of ContactUs relative to the viewport:
      const rect = contactEl.getBoundingClientRect();
      const topOfContact = rect.top; // distance from viewport top to ContactUs top
      const viewportHeight = window.innerHeight;

      // If the top of ContactUs is below the bottom edge of the viewport minus BAR_HEIGHT,
      // then there's no overlap yet. Keep bottomOffset = 10.
      // Otherwise, contact section is “rising up” into our fixed bar area.
      if (topOfContact > viewportHeight - BAR_HEIGHT) {
        // No overlap
        setBottomOffset(10);
      } else {
        // There IS overlap: compute how many pixels into the bar we would collide.
        //  For example, if topOfContact = 700px, and viewportHeight = 800px, BAR_HEIGHT = 50,
        //  then (viewportHeight - BAR_HEIGHT) = 750. 700 < 750 ⇒ overlap zone.
        //  The overlap amount = (BAR_HEIGHT - (viewportHeight - topOfContact)).
        //  But a simpler way: overlapPx = (viewportHeight - topOfContact) - (BAR_HEIGHT - 10).
        //  We want the bar’s bottom to be pushed up by that overlap + a 10px gap.
        const overlapAmount = viewportHeight - topOfContact - (BAR_HEIGHT - 10);

        // We add that overlapAmount to 10px, so the final bottomOffset is always ≥ 10.
        // Clamp it so it never becomes negative or too huge:
        const newOffset = Math.max(10, overlapAmount + 10);
        setBottomOffset(newOffset);
      }
    };

    // Attach the scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Run it once on mount in case the user is already scrolled
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // empty‐deps ⇒ set up once at mount

  // ─────────────────────────────────────────────────────────────────────
  // All the rest of your code is unchanged, except that whenever you
  // would have used “bottom: 10” in your sx for mobile, use bottomOffset.
  // ─────────────────────────────────────────────────────────────────────
  const mobileStyles = useMemo(
    () => ({
      position: "fixed",
      bottom: `${bottomOffset}px`, // <— use our dynamic bottomOffset
      left: "50%",
      transform: "translateX(-50%)",
      width: "90%",
      height: "50px", // matches BAR_HEIGHT
      flexDirection: "row",
    }),
    [bottomOffset]
  );

  const desktopStyles = useMemo(
    () => ({
      position: "fixed",
      right: 10,
      top: "50%",
      transform: "translateY(-50%)",
      height: "60%",
      width: "60px",
      flexDirection: "column",
    }),
    []
  );

  const progressValue = useMemo(
    () => (hasNextDiscount ? animatedProgress : 100),
    [hasNextDiscount, animatedProgress]
  );

  const logoPosition = useMemo(() => {
    const baseCalc = `calc(${animatedProgress}% - ${
      animatedProgress === 0 ? "20px" : "40px"
    })`;
    return isMobile
      ? { left: baseCalc, rotate: "-12deg" }
      : { bottom: baseCalc, rotate: "-12deg" };
  }, [isMobile, animatedProgress]);

  const progressStyle = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      position: "absolute",
      bottom: 0,
      left: 0,
      borderRadius: "20px",
      backgroundColor: "rgba(224, 224, 224, 0.3)",
      "& .MuiLinearProgress-bar": {
        borderRadius: "20px",
        backgroundColor: "#1b7a3d",
        transform: isMobile
          ? `translateX(${progressValue - 100}%) !important`
          : `translateY(${100 - progressValue}%) !important`,
        transition: "transform 0.15s linear",
      },
    }),
    [isMobile, progressValue]
  );

  const discountMessage = useMemo(() => {
    if (!hasAnyDiscounts) {
      return null;
    }
    if (!hasNextDiscount) {
      return t("discount achieved!");
    }
    if (nextDiscount && typeof amountTillNextDiscount === "number") {
        // console.log(amountTillNextDiscount)
      return t("add {{amount}}₪ more {{discount}}% off", {
        amount: amountTillNextDiscount.toFixed(0),
        discount: nextDiscount.value,
      });
    }
    return t("Loading...");
  }, [
    hasAnyDiscounts,
    hasNextDiscount,
    nextDiscount,
    amountTillNextDiscount,
    t,
  ]);
  if(!hasAnyDiscounts) return null;
  return (
    <Box
      sx={{
        ...(isMobile ? mobileStyles : desktopStyles),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: isMobile ? "row" : "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          sx={{
            height: isMobile ? "30px" : "100%",
            width: isMobile ? "100%" : "30px",
            position: "relative",
            backgroundColor: "rgba(224, 224, 224, 0.5)",
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
          }}
        >
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={progressStyle}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: isMobile ? "0 10px" : "10px 0",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                transform: isMobile ? "none" : "rotate(-90deg)",
                whiteSpace: "nowrap",
                fontSize: isMobile ? "0.8rem" : "1rem",
                color: "rgba(0, 0, 0, 0.7)",
                fontWeight: 550,
                textShadow: "1px 1px 2px rgba(255,255,255,0.5)",
              }}
            >
              {discountMessage}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            ...logoPosition,
            transition: "all 0.15s linear",
          }}
        >
          <img
            src="/logo512.png"
            alt="Logo"
            style={{
              width: "50px",
              height: "50px",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
});

export default DiscountProgressBar;
